import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { showToast } from '../../components/toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_URL } from "../../config";

const EditDynemicBlogPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const hasShownToast = useRef(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    post_title: '',
    new_content: '',
    html_data: '',
    html_content: ''
  });

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  useEffect(() => {
    if (id) {
      fetchDynamicBlogData();
    }
  }, [id]);

  const fetchDynamicBlogData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}dynamicBlogs/add/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.success) {
        const blogData = response.data.data.dynamicblogs || response.data.data;

        setFormData({
          post_title: blogData.post_title || '',
          new_content: blogData.new_content || '',
          html_data: blogData.html_data || '',
          html_content: blogData.html_content || ''
        });

        if (!hasShownToast.current) {
          showToast.success('Data fetched successfully');
          hasShownToast.current = true;
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch blog data';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that we have an ID and it's valid
    if (!id || isNaN(parseInt(id))) {
      showToast.error('Invalid blog ID');
      return;
    }

    // Validate that we have content to save
    if (!formData.new_content || formData.new_content.trim() === '') {
      showToast.error('Please enter content before saving');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `${API_URL}dynamicBlogs/save`,
        {
          id: parseInt(id), // Convert string to number
          new_content: formData.new_content
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.success) {
        showToast.success('Blog saved successfully');
        navigate('/DynamicBlogs');
      } else {
        // Handle case where response doesn't indicate success
        const errorMessage = response.data?.message || 'Failed to save blog data';
        showToast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save blog data';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendData = async () => {
    // Validate ID before sending
    if (!id || isNaN(parseInt(id))) {
      showToast.error('Invalid blog ID');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('authToken');

      const response = await axios.get(
        `${API_URL}dynamicBlogs/push-dynamic-blog/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.status === 200) {
        showToast.success(response.data.message || 'Dynamic Blog pushed successfully');
        fetchDynamicBlogData(); // Refresh data after sending
      } else {
        showToast.error(response.data.message || 'Failed to push Dynamic Blog');
      }
    } catch (error) {
      console.error('Error sending data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to push Dynamic Blog. Please try again.';
      showToast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div id="content" className="" style={{ marginTop: "40px" }}>
      <div className="panel panel-default with-rounded-corner with-shadow">
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="id" value={id} />
          <div className="panel-heading">
            <h4 className="panel-title pull-left">
              {id ? 'Edit Page' : 'Add Page'}
            </h4>
            {id && (
              <button
                type="button"
                className="btn btn-default btn-sm pull-right"
                onClick={handleSendData}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  'Send Data'
                )}
              </button>
            )}
          </div>
          <div className="clearfix"></div>
          <div className="panel-body">
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label>Post Title</label>
                  <div className="form-control" style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.25rem',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {formData.post_title || 'No title available'}
                  </div>
                </div>

                <div className="form-group">
                  <label>New Content</label>
                  <div className="quill-editor-container">
                    <ReactQuill
                      theme="snow"
                      value={formData.new_content}
                      onChange={(content) => setFormData({ ...formData, new_content: content })}
                      modules={modules}
                      formats={formats}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Api Content</label>
                  <div className="quill-editor-container">
                    <ReactQuill
                      theme="snow"
                      value={formData.html_data}
                      onChange={(content) => setFormData({ ...formData, html_data: content })}
                      modules={modules}
                      formats={formats}
                      readOnly={true}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => navigate('/DynamicBlogs')}
                  >
                    Cancle
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {formData.html_content && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="row">
            <div className="col-md-12">
              <div style={{ padding: '20px' }}>
                <h3>Original Content</h3>
                <div dangerouslySetInnerHTML={{ __html: formData.html_content }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default EditDynemicBlogPage;
